import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getMyBookings, cancelBooking } from '../api';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

const STATUS_STYLES = {
  confirmed: { bg: '#dcfce7', color: '#166534', label: 'Confirmed' },
  pending: { bg: '#fef9c3', color: '#854d0e', label: 'Pending' },
  cancelled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
  completed: { bg: '#dbeafe', color: '#1e40af', label: 'Completed' },
  expired: { bg: '#f3f4f6', color: '#6b7280', label: 'Expired' },
};

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.expired;
  return (
    <span
      className="inline-block px-3 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {style.label}
    </span>
  );
}

function BookingCard({ booking, onCancel, cancelling }) {
  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (d) => {
    const date = new Date(d + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isUpcoming = booking.status === 'confirmed' || booking.status === 'pending';
  const bookingDateTime = new Date(`${booking.booking_date}T${booking.start_time}`);
  const isFuture = bookingDateTime > new Date();
  const canCancel = isUpcoming && isFuture;

  return (
    <div
      className="rounded-xl p-5 shadow-sm border transition-all hover:shadow-md"
      style={{ backgroundColor: 'var(--color-bg-alt)', borderColor: '#e5e7eb' }}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>
              {booking.service_name || 'Sauna Session'}
            </h3>
            <StatusBadge status={booking.status} />
          </div>

          <div className="space-y-1 text-sm" style={{ color: 'var(--color-text-light)' }}>
            <p className="flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formatDate(booking.booking_date)}
            </p>
            <p className="flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
            </p>
            {booking.delivery_address && (
              <p className="flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {booking.delivery_address}
              </p>
            )}
            {booking.addons && booking.addons.length > 0 && (
              <p className="flex items-start gap-2">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>{booking.addons.map((a) => a.addon_name).join(', ')}</span>
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <span className="text-xl font-bold" style={{ color: 'var(--color-accent)' }}>
            ${Number(booking.amount_paid).toFixed(2)}
          </span>
          {canCancel && (
            <button
              onClick={() => onCancel(booking.id)}
              disabled={cancelling === booking.id}
              className="px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors hover:bg-red-50 disabled:opacity-50"
              style={{ borderColor: '#fca5a5', color: '#dc2626' }}
            >
              {cancelling === booking.id ? 'Cancelling...' : 'Cancel'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MyBookingsPage() {
  const { customer, token, loading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(null);
  const [cancelError, setCancelError] = useState('');

  const fetchBookings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await getMyBookings(token);
      setBookings(data);
    } catch {
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (authLoading) return;
    if (!customer) {
      navigate('/login', { state: { from: '/my-bookings' }, replace: true });
      return;
    }
    fetchBookings();
  }, [customer, authLoading, navigate, fetchBookings]);

  const handleCancel = async (id) => {
    setCancelling(id);
    setCancelError('');
    try {
      await cancelBooking(id, token);
      setBookings((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status: 'cancelled' } : b))
      );
    } catch (err) {
      setCancelError(err.message || 'Could not cancel booking.');
    } finally {
      setCancelling(null);
    }
  };

  const now = new Date();
  const upcoming = bookings.filter((b) => {
    const dt = new Date(`${b.booking_date}T${b.start_time}`);
    return dt >= now && (b.status === 'confirmed' || b.status === 'pending');
  });
  const past = bookings.filter((b) => {
    const dt = new Date(`${b.booking_date}T${b.start_time}`);
    return dt < now || (b.status !== 'confirmed' && b.status !== 'pending');
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className="w-8 h-8 border-4 rounded-full animate-spin"
          style={{ borderColor: 'var(--color-bg-alt)', borderTopColor: 'var(--color-accent)' }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
              My Bookings
            </h1>
            {customer && (
              <p className="mt-1" style={{ color: 'var(--color-text-light)' }}>
                Welcome back, {customer.name}
              </p>
            )}
          </div>
          <Link
            to="/book"
            className="inline-block px-6 py-2.5 rounded-xl text-white font-semibold text-sm transition-opacity hover:opacity-90 text-center"
            style={{ backgroundColor: 'var(--color-accent)' }}
          >
            Book Another Session
          </Link>
        </div>

        {error && (
          <div className="rounded-lg p-3 mb-6 bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {cancelError && (
          <div className="rounded-lg p-3 mb-6 bg-red-50 border border-red-200 text-red-700 text-sm">
            {cancelError}
          </div>
        )}

        {bookings.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-text-light)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text)' }}>
              No bookings yet
            </h2>
            <p className="mb-6" style={{ color: 'var(--color-text-light)' }}>
              Ready to warm up? Book your first sauna session today.
            </p>
            <Link
              to="/book"
              className="inline-block px-8 py-3 rounded-xl text-white font-bold text-lg transition-opacity hover:opacity-90"
              style={{ backgroundColor: 'var(--color-accent)' }}
            >
              Book Now
            </Link>
          </div>
        ) : (
          <>
            {/* Upcoming */}
            <section className="mb-10">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--color-primary)' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Upcoming
              </h2>
              {upcoming.length === 0 ? (
                <p className="text-sm italic" style={{ color: 'var(--color-text-light)' }}>
                  No upcoming bookings.
                </p>
              ) : (
                <div className="space-y-4">
                  {upcoming.map((b) => (
                    <BookingCard key={b.id} booking={b} onCancel={handleCancel} cancelling={cancelling} />
                  ))}
                </div>
              )}
            </section>

            {/* Past */}
            {past.length > 0 && (
              <section>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-light)' }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Past
                </h2>
                <div className="space-y-4 opacity-80">
                  {past.map((b) => (
                    <BookingCard key={b.id} booking={b} onCancel={handleCancel} cancelling={cancelling} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
